import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Image as ImageIcon, Send, UserCircle, Film, Hash, Search, ChevronDown, BarChart3 } from 'lucide-react';
import {
  buildPoll,
  MAX_POLL_OPTIONS,
  MIN_POLL_OPTIONS,
  MAX_POLL_OPTION_LENGTH,
  MAX_POLL_DAYS,
  MAX_ANONYMOUS_POLL_DAYS,
} from '../services/PollService';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { CommunityDiscoveryService } from '../services/CommunityDiscoveryService';
import { formatMemberCount } from './utils/memberCount';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
// Checkbox removed
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { YapService } from '../services/api';
import { useTheme } from './ThemeProvider';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { StorageService } from '../services/StorageService';
import { triggerHaptic } from '../utils/haptic';
import { MediaTrimmerModal } from './MediaTrimmerModal';
import { videoBlobToSrc } from '../utils/videoPreviewUrl';
import { yapCharLimit } from '../constants/posts';

interface CreateYapModalProps {
  isOpen: boolean;
  onClose: () => void;
  visibility: 'public' | 'friends';
  groupId?: string;
  groupType?: 'forum' | 'tribe';
}

const MAX_IMAGES_DEFAULT = 4;
const MAX_IMAGES_GROUP   = 6;
// Hard 3-minute cap on attached videos. Anything longer is presented to the
// user with a start-time slider so they can pick the 3-minute window they want
// to keep — the upload itself is the full file; the yap doc stores trim
// metadata that playback honours (same pattern as portfolio video trims).
const MAX_VIDEO_SECONDS = 180;
const MAX_VIDEO_BYTES   = 100 * 1024 * 1024; // 100 MB hard cap on upload size

export function CreateYapModal({ isOpen, onClose, visibility, groupId, groupType }: CreateYapModalProps) {
  // Group yaps (tribe / forum) get a higher image cap.
  const MAX_IMAGES = (groupId && groupType) ? MAX_IMAGES_GROUP : MAX_IMAGES_DEFAULT;
  const [content, setContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  // Video attachment state. selectedVideo is the raw File (uploaded to Storage
  // on post). videoPreviewUrl is an object URL for the inline <video> preview.
  // videoDuration is the natural full-file duration in seconds.
  // videoTrimStart is the user-chosen window start; the window end is always
  // min(videoTrimStart + 180, videoDuration).
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  // Real video pixel dimensions captured at select time — persisted on the yap
  // so the player can size correctly on first paint (no aspect-ratio flash).
  const [videoWidth, setVideoWidth] = useState<number>(0);
  const [videoHeight, setVideoHeight] = useState<number>(0);
  const [videoTrimStart, setVideoTrimStart] = useState<number>(0);
  // Explicit end set by the "Trim Video" trimmer (overrides the derived 3-min
  // window). null = use the derived window.
  const [videoTrimEndOverride, setVideoTrimEndOverride] = useState<number | null>(null);
  const [showVideoTrimmer, setShowVideoTrimmer] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  // Cleanup for the current video preview src (revokes the blob URL / deletes
  // the temp cache file the helper created). Called before replacing or clearing.
  const previewCleanupRef = useRef<(() => void) | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [mounted, setMounted] = useState(false);

  // ── Poll ───────────────────────────────────────────────────────────────────
  const [pollOpen, setPollOpen] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollDays, setPollDays] = useState(1);
  // An anonymous yap is hard-deleted 24h after posting, taking the poll and every
  // vote with it, so a poll on one cannot be offered a longer run than that.
  const maxPollDays = isAnonymous ? MAX_ANONYMOUS_POLL_DAYS : MAX_POLL_DAYS;
  useEffect(() => {
    if (pollDays > maxPollDays) setPollDays(maxPollDays);
  }, [maxPollDays, pollDays]);
  const { theme } = useTheme();

  // ── Forum "topic" tagging (YapTab only — not when composing inside a group) ──
  // selectedTopic is the chosen forum; the yap cross-posts there while still
  // living in the main feed. The forum list is loaded once when the modal opens
  // and filtered locally (no per-keystroke Firestore query).
  const [selectedTopic, setSelectedTopic] = useState<{ forumId: string; forumName: string } | null>(null);
  const [topicPickerOpen, setTopicPickerOpen] = useState(false);
  const [forums, setForums] = useState<any[]>([]);
  const [forumsLoaded, setForumsLoaded] = useState(false);
  const [topicSearch, setTopicSearch] = useState('');

  // 🎙️ VOICE RECORDING STATE
  const { isRecording, isPaused, duration, startRecording, stopRecording, pauseRecording, resumeRecording, audioBlob, reset, error } = useVoiceRecorder(10000);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!audioBlob || isRecording) return;
    const url = URL.createObjectURL(audioBlob);
    setVoicePreviewUrl(url);
    setVoiceBlob(audioBlob);
  }, [audioBlob, isRecording]);

  useEffect(() => {
    return () => {
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    };
  }, [voicePreviewUrl]);

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('hideFloatingUI'));
    } else {
      window.dispatchEvent(new CustomEvent('showFloatingUI'));
    }
  }, [isOpen]);

  // Load the forum list once, when a YapTab compose opens (topic tagging is not
  // offered inside a group). Filtering happens locally on the loaded list.
  useEffect(() => {
    if (!isOpen || groupId || forumsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await CommunityDiscoveryService.getCommunities(
          'forums', 'members', userProfile?.userTags || [], 100
        );
        if (!cancelled) { setForums(results || []); setForumsLoaded(true); }
      } catch (e) {
        console.warn('[CreateYapModal] Failed to load forums for topic picker:', e);
        if (!cancelled) setForumsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, groupId, forumsLoaded]);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
      window.dispatchEvent(new CustomEvent('showFloatingUI')); // Safety cleanup
    };
  }, []);

  // 1000 characters, no limit for Legion (constants/posts.ts).
  const { userProfile: author } = useUserData();
  const charLimit = yapCharLimit(author);
  const remainingChars = charLimit === null ? Infinity : charLimit - content.length;
  // Only one video OR up to MAX_IMAGES images per yap. Once a video is attached
  // the image button is disabled, and vice versa.
  const canAddImage = !selectedVideo && selectedImages.length < MAX_IMAGES;
  const canAddVideo = !selectedVideo && selectedImages.length === 0;

  const mediaInputRef = useRef<HTMLInputElement>(null);

  const handleMediaUpload = () => {
    if (canAddImage || canAddVideo) {
      mediaInputRef.current?.click();
    }
  };

  // Probe a video file for its duration without loading the whole thing into
  // memory. Returns seconds; rejects on metadata-load errors.
  const probeVideoDuration = (file: File): Promise<{ duration: number; width: number; height: number }> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({ duration: v.duration, width: v.videoWidth, height: v.videoHeight });
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video metadata'));
    };
    v.src = url;
  });

  // Build a renderable preview src for a video blob/file. On native iOS this
  // writes a temp cache file and uses convertFileSrc (blob: URLs render black in
  // WKWebView); on web it's a blob URL. Cleans up the previous preview first.
  const applyVideoPreview = async (blob: Blob) => {
    try { previewCleanupRef.current?.(); } catch (_) {}
    previewCleanupRef.current = null;
    const { src, cleanup } = await videoBlobToSrc(blob);
    previewCleanupRef.current = cleanup;
    setVideoPreviewUrl(src);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Branch on MIME type — same picker accepts images and videos.
    if (file.type.startsWith('video/')) {
      if (!canAddVideo) {
        toast.error('Only one video per yap, and not mixed with images.');
        if (mediaInputRef.current) mediaInputRef.current.value = '';
        return;
      }
      if (file.size > MAX_VIDEO_BYTES) {
        toast.error('Video file is too large (max 100MB).');
        if (mediaInputRef.current) mediaInputRef.current.value = '';
        return;
      }
      try {
        const { duration, width, height } = await probeVideoDuration(file);
        if (!isFinite(duration) || duration <= 0) {
          toast.error('Could not read video duration.');
          return;
        }
        setSelectedVideo(file);
        await applyVideoPreview(file);
        setVideoDuration(duration);
        setVideoWidth(width || 0);
        setVideoHeight(height || 0);
        setVideoTrimStart(0);
        setVideoTrimEndOverride(null);
        // Warm up ffmpeg.wasm in the background now, so trimming is fast later.
        import('../services/videoTrim').then(m => m.preloadFFmpeg()).catch(() => {});
        if (duration > MAX_VIDEO_SECONDS) {
          toast.info('Video is longer than 3 minutes — choose the 3-minute window to keep below.');
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to load video.');
      } finally {
        if (mediaInputRef.current) mediaInputRef.current.value = '';
      }
      return;
    }
    // Image branch — preserves the original behaviour.
    if (!canAddImage) {
      toast.error(selectedVideo ? 'Remove the video first to add images.' : `Max ${MAX_IMAGES} images.`);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImages(prev => [...prev, reader.result as string]);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (index: number) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
  };

  const removeVideo = () => {
    try { previewCleanupRef.current?.(); } catch (_) {}
    previewCleanupRef.current = null;
    setSelectedVideo(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    setVideoTrimStart(0);
    setVideoTrimEndOverride(null);
  };

  // Cleanup the preview src (blob URL / temp file) on unmount so it doesn't leak.
  useEffect(() => {
    return () => { try { previewCleanupRef.current?.(); } catch (_) {} };
  }, []);

  // When the trim window changes, jump the compose preview to the trimmed
  // section's first frame so the thumbnail reflects the trim.
  useEffect(() => {
    const v = previewVideoRef.current;
    if (v && isFinite(videoTrimStart)) {
      try { v.currentTime = videoTrimStart; } catch (_) {}
    }
  }, [videoTrimStart, videoTrimEndOverride, videoPreviewUrl]);

  const videoTrimEnd = videoTrimEndOverride ?? Math.min(videoTrimStart + MAX_VIDEO_SECONDS, videoDuration);
  const videoNeedsTrim = videoDuration > MAX_VIDEO_SECONDS;
  const handlePost = async () => {
    if (content.trim().length === 0 && !voiceBlob && !selectedVideo) {
      toast.error('Add some text, a voice clip, or a video before posting!');
      return;
    }

    if (charLimit !== null && content.length > charLimit) {
      toast.error(`Your yap exceeds the ${charLimit} character limit!`);
      return;
    }

    // Validated with the other pre-flight checks, before the composer closes: a
    // half-filled builder should stop the post rather than silently drop the
    // poll, which would publish a yap the author thought had one attached.
    const pollPayload = pollOpen
      ? buildPoll({ options: pollOptions, days: Math.min(pollDays, maxPollDays) })
      : null;
    if (pollOpen && !pollPayload) {
      toast.error(`A poll needs at least ${MIN_POLL_OPTIONS} filled options.`);
      return;
    }

    const loadingToast = toast.loading('Posting your yap…');

    // 🚀 Don't block the UI on the (possibly slow) media upload: close the
    // composer immediately and let the post finish uploading in the background.
    // This handler's closure keeps the captured File/blobs + form data, so the
    // upload completes correctly even after the modal unmounts. The feed
    // refreshes via the 'yapCreated' event on success. (Fixes the "stuck staring
    // at the compose modal" + the post-publish app reload.)
    if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    try { previewCleanupRef.current?.(); } catch (_) {}
    previewCleanupRef.current = null;
    setContent('');
    setSelectedImages([]);
    setIsAnonymous(false);
    setPollOpen(false);
    setPollOptions(['', '']);
    setPollDays(1);
    setSelectedTopic(null);
    setTopicPickerOpen(false);
    setTopicSearch('');
    setVoiceBlob(null);
    setVoicePreviewUrl(null);
    setSelectedVideo(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    setVideoTrimStart(0);
    setVideoTrimEndOverride(null);
    reset();
    onClose();

    // Post the yap (background)
    try {
      const locationText = userProfile.showLocation === false
        ? 'Earth'
        : (userProfile.home_residence?.trim() || userProfile.location?.trim() || userProfile.country?.trim() || 'Earth');

      const yapData: any = {
        content,
        visibility,
        ...(pollPayload ? { poll: pollPayload } : {}),
        authorCountryFlag: userProfile.countryFlag || userProfile.country,
        location: locationText,
        home_residence: userProfile.home_residence || '',
        showLocation: userProfile.showLocation ?? true,
      };

      // 🎙️ Handle Voice Upload
      if (voiceBlob) {
        const userIdForStorage = userProfile?.id;
        if (userIdForStorage) {
          const audioUrl = await StorageService.uploadVoiceReply(String(userIdForStorage), voiceBlob);
          yapData.audioUrl = audioUrl;
          yapData.duration = duration;
          yapData.type = 'voice';
        }
      }

      // Pass all selected images as an array (backend uploads each to Firebase Storage)
      if (selectedImages.length === 1) {
        yapData.image = selectedImages[0];
      } else if (selectedImages.length > 1) {
        yapData.images = selectedImages;
      }

      // 🎬 Handle Video Upload — exactly one per yap. When the user trims, the
      // file is physically cut on-device (ffmpeg.wasm) and that trimmed blob is
      // what's uploaded here, so no trim markers are needed. The metadata-based
      // trim (videoTrimStart/End) only persists as a fallback if the on-device
      // cut failed.
      if (selectedVideo) {
        const userIdForStorage = userProfile?.id;
        if (userIdForStorage) {
          const videoUrl = await StorageService.uploadYapVideo(String(userIdForStorage), selectedVideo);
          yapData.videoUrl = videoUrl;
          // Store the (trimmed) duration so the yap reflects the real length.
          if (videoDuration > 0) yapData.videoDuration = videoDuration;
          // Persist real pixel dimensions so the player sizes to the correct
          // aspect ratio on first paint (no flash). Trimming changes time, not
          // resolution, so the select-time dimensions stay valid.
          if (videoWidth > 0 && videoHeight > 0) {
            yapData.videoWidth = videoWidth;
            yapData.videoHeight = videoHeight;
          }
          // Persist trim metadata only as a fallback (ffmpeg failed) or for an
          // untrimmed >3-min video that needs the default 3-min window.
          if (videoNeedsTrim || videoTrimEndOverride !== null) {
            yapData.videoTrimStart = videoTrimStart;
            yapData.videoTrimEnd = videoTrimEnd;
          }
        }
      }

      // Add anonymous yap fields if posting anonymously
      if (isAnonymous && visibility === 'public') {
        // Block anonymous posts in groups
        if (groupId && groupType) {
          toast.error(`Anonymous posts are not allowed in ${groupType === 'forum' ? 'Forums' : 'Tribes'}. Please uncheck "Post Anonymously".`);
          return;
        }

        // Double check limit before posting
        if (!canPostAnonymously()) {
          toast.error(`Daily anonymous post limit reached. Try again in ${getTimeUntilNextAnonymousPost()}`);
          return;
        }

        yapData.isAnonymous = true;
        // Anonymous yaps live exactly 24h from the moment they're posted, then a
        // scheduled Cloud Function hard-deletes them (post + replies + likes).
        // Previously this was pinned to the end of the current YapDay, which
        // meant a yap posted late in the day vanished in minutes while an
        // early one lasted nearly a full day.
        yapData.expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      }

      // Add group context if posting within a forum or tribe
      if (groupId && groupType) {
        if (groupType === 'forum') {
          yapData.forumId = groupId;
        } else if (groupType === 'tribe') {
          yapData.tribeId = groupId;
        }
        // Default to NOT cross-posted - keeps group yaps local
        yapData.isCrossPosted = false;
      }

      // Forum topic tag — only for YapTab posts (not group composes). The yap
      // stays in the main feed and is surfaced in the tagged forum's feed.
      if (selectedTopic && !groupId) {
        yapData.forumTopic = selectedTopic;
      }

      await YapService.createYap(yapData);

      triggerHaptic('success'); // 📳 iOS parity: haptic on successful publish
      toast.dismiss(loadingToast);
      toast.success(
        isAnonymous
          ? `Your anonymous yap has been posted! It disappears in 24 hours. 🕵️`
          : visibility === 'public'
            ? 'Your yap has been posted to the world! 🌍'
            : 'Your yap has been shared with friends! 👥'
      );

      // Dispatch event to refresh feed (composer already closed above).
      window.dispatchEvent(new CustomEvent('yapCreated'));
    } catch (error) {
      toast.dismiss(loadingToast);
      triggerHaptic('error'); // 📳 iOS parity: haptic on post failure
      console.error("Error creating yap:", error);
      toast.error("Failed to post yap. Please try again.");
    }
  };

  const { userProfile, canPostAnonymously, getAnonymousPostsRemaining, getTimeUntilNextAnonymousPost } = useUserData();

  const forumName = (f: any) => f?.name || f?.title || 'Forum';
  const filteredForums = forums.filter((f) => {
    const q = topicSearch.trim().toLowerCase();
    if (!q) return true;
    return forumName(f).toLowerCase().includes(q);
  });

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            /* Inline: `z-9999` has no rule in the committed pre-compiled
               index.css, so this modal had no stacking context at all. */
            style={{ zIndex: 9999 }}
            onClick={onClose}
          >
          <motion.div
            data-tour="guide-post-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-light-pattern dark:bg-[#121212] rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-black/10 dark:border-[#4478ea]/30 modal-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — fixed */}
            <div className="shrink-0 px-6 pt-6 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl text-[#ea580c] dark:text-white">
                  Create a Yap
                </h2>
                <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">
                  {visibility === 'public' ? '🌍 Visible to everyone' : '👥 Visible to friends only'}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
              >
                <X className="w-5 h-5 text-[#ea580c] dark:text-white" />
              </motion.button>
            </div>
            </div>{/* end header */}

            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-12 h-12 border-2 border-[#F39C12] dark:border-[#4478ea]">
                <AvatarImage src={userProfile.portfolio[userProfile.profilePictureIndex || 0]} />
                <AvatarFallback>{userProfile.name[0]}</AvatarFallback>
              </Avatar>
              <div>
                <h4 className="text-[#ea580c] dark:text-white">{userProfile.name}</h4>
                <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">{userProfile.username}</p>
              </div>
            </div>
            <div className="mb-4">
              <Textarea
                placeholder={voicePreviewUrl ? "Add a caption to your voice note..." : "What's on your mind?"}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[120px] bg-[#F5F5F5] dark:bg-black/30 border-black/20 dark:border-white/20 text-[#ea580c] dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] resize-none text-lg whitespace-pre-wrap"
                maxLength={charLimit ?? undefined}
              />

              {/* 🎙️ VOICE PREVIEW */}
              {voicePreviewUrl && (
                <div className="mt-4 relative group w-fit">
                  <VoiceReplyBubble 
                    audioUrl={voicePreviewUrl} 
                    duration={duration} 
                    className="pr-10"
                  />
                  <button
                    onClick={() => {
                      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
                      setVoicePreviewUrl(null);
                      setVoiceBlob(null);
                      reset();
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* @Mention Indicator while typing */}
              {content.match(/@[\w\.]+/g) && (
                <motion.div 
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 flex flex-wrap gap-1.5"
                >
                  <span className="text-[10px] text-[#666666] dark:text-[#999999] uppercase font-bold flex items-center mr-1"> Mentioning: </span>
                  {Array.from(new Set(content.match(/@[\w\.]+/g))).map((mention, i) => (
                    <span 
                      key={i}
                      className="px-2 py-0.5 rounded-full bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/20 dark:border-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea] text-[10px] font-bold"
                    >
                      {mention}
                    </span>
                  ))}
                </motion.div>
              )}
              <div className="flex items-center justify-between mt-2">
                <p className={`text-sm ${remainingChars < 50
                  ? 'text-[#E74C3C]'
                  : 'text-[#666666] dark:text-[#E5E5E5]'
                  }`}>
                  {charLimit === null ? 'No character limit' : `${remainingChars} characters remaining`}
                </p>
                <p className="text-xs text-[#999999] dark:text-[#E5E5E5]">
                  {charLimit === null ? content.length : `${content.length}/${charLimit}`}
                </p>
              </div>
            </div>

            {/* ── Forum Topic tagging (public YapTab posts only; works with anonymous) ── */}
            {visibility === 'public' && !groupId && (
              <div className="mb-4">
                {selectedTopic ? (
                  /* Selected topic chip */
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 pl-4 pr-2.5 py-1.5 rounded-full bg-[#4478ea]/10 dark:bg-[#4478ea]/15 border border-[#4478ea]/25 text-[#4478ea] text-sm font-bold max-w-full">
                      <Hash className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{selectedTopic.forumName}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedTopic(null)}
                        className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/10 transition-colors shrink-0"
                        aria-label="Remove topic"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Add Topic toggle */
                  <motion.button
                    data-tour="guide-post-topics"
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setTopicPickerOpen((o) => !o)}
                    className={`w-full p-4 rounded-xl border transition-all duration-200 flex items-center justify-between ${topicPickerOpen
                      ? 'bg-[#4478ea]/10 dark:bg-[#4478ea]/15 border-[#4478ea]'
                      : 'bg-gray-200 dark:bg-slate-800 border-transparent hover:bg-gray-300 dark:hover:bg-slate-700'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-black/5 dark:bg-[#eddeb7]/10 text-[#666666] dark:text-[#999999]">
                        <Hash className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-sm font-medium text-[#4478ea] dark:text-[#4478ea]">Add Topic</h4>
                        <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">Cross-post this yap to a Forum</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-[#666666] dark:text-[#999999] transition-transform ${topicPickerOpen ? 'rotate-180' : ''}`} />
                  </motion.button>
                )}

                {/* Picker panel */}
                <AnimatePresence>
                  {topicPickerOpen && !selectedTopic && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 rounded-xl border border-black/10 dark:border-white/10 bg-[#F5F5F5] dark:bg-black/30 p-2">
                        <div className="relative mb-2">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
                          <Input
                            value={topicSearch}
                            onChange={(e) => setTopicSearch(e.target.value)}
                            placeholder="Search forums..."
                            className="pl-9 h-10 bg-white dark:bg-black/40 border-black/10 dark:border-white/10 text-[#ea580c] dark:text-white placeholder:text-[#666666] dark:placeholder:text-[#888888] rounded-lg"
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                          {!forumsLoaded ? (
                            <p className="text-xs text-[#999999] text-center py-6">Loading forums…</p>
                          ) : filteredForums.length === 0 ? (
                            <p className="text-xs text-[#999999] text-center py-6">
                              {forums.length === 0 ? 'No forums available yet.' : 'No forums match your search.'}
                            </p>
                          ) : (
                            filteredForums.map((f) => (
                              <button
                                key={String(f.id)}
                                type="button"
                                onClick={() => {
                                  setSelectedTopic({ forumId: String(f.id), forumName: forumName(f) });
                                  setTopicPickerOpen(false);
                                  setTopicSearch('');
                                }}
                                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <Hash className="w-4 h-4 text-[#4478ea] shrink-0" />
                                  <span className="text-sm font-medium text-[#ea580c] dark:text-white truncate">{forumName(f)}</span>
                                </span>
                                <span className="text-xs text-[#999999] shrink-0">{formatMemberCount(f.memberCount || 0)}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Anonymous Posting Toggle - Only show for public visibility AND not in groups */}
            {visibility === 'public' && !groupId && (
              <div className="mb-4">
                <motion.button
                  data-tour="guide-post-anonymous"
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (canPostAnonymously() || isAnonymous) {
                      setIsAnonymous(!isAnonymous);
                    } else {
                      toast.error(`Limit reached! Next anonymous post available in ${getTimeUntilNextAnonymousPost()}`);
                    }
                  }}
                  disabled={!canPostAnonymously() && !isAnonymous}
                  className={`w-full p-4 rounded-xl border transition-all duration-200 flex items-center justify-between group ${isAnonymous
                    ? 'bg-linear-to-r from-[#F39C12]/20 to-[#E74C3C]/20 dark:from-[#4478ea]/20 dark:to-[#2d1ef1]/20 border-[#F39C12] dark:border-[#4478ea]'
                    : 'bg-gray-200 dark:bg-slate-800 border-transparent hover:bg-gray-300 dark:hover:bg-slate-700'
                    } ${!canPostAnonymously() && !isAnonymous ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isAnonymous
                      ? 'bg-[#F39C12]/20 dark:bg-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea]'
                      : 'bg-black/5 dark:bg-[#eddeb7]/10 text-[#666666] dark:text-[#999999]'
                      }`}>
                      <UserCircle className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <h4 className={`text-sm font-medium ${isAnonymous ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-[#ea580c] dark:text-white'
                        }`}>
                        Post Anonymously
                      </h4>
                      <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                        {isAnonymous
                          ? `Identity hidden • Deleted after 24h`
                          : 'Hide your identity for this yap'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <div className={`text-xs font-medium px-2 py-1 rounded-full ${getAnonymousPostsRemaining() > 0
                      ? 'bg-[#F39C12]/10 dark:bg-[#4478ea]/10 text-[#F39C12] dark:text-[#4478ea]'
                      : 'bg-red-500/10 text-red-500'
                      }`}>
                      {getAnonymousPostsRemaining()}/20 remaining
                    </div>
                    {!canPostAnonymously() && (
                      <span className="text-[10px] text-[#666666] dark:text-[#999999] mt-1">
                        Next in {getTimeUntilNextAnonymousPost()}
                      </span>
                    )}
                  </div>
                </motion.button>
              </div>
            )}

            {/* Poll Toggle — same switchable-button style as Post Anonymously
                above, and available in groups too, since a poll travels with the
                yap wherever it appears. */}
            <div className="mb-4">
              <motion.button
                data-tour="guide-post-polls"
                whileTap={{ scale: 0.98 }}
                onClick={() => setPollOpen((o) => !o)}
                className={`w-full p-4 rounded-xl border transition-all duration-200 flex items-center justify-between ${pollOpen
                  ? 'bg-[#4478ea]/10 dark:bg-[#4478ea]/15 border-[#4478ea]'
                  : 'bg-gray-200 dark:bg-slate-800 border-transparent hover:bg-gray-300 dark:hover:bg-slate-700'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${pollOpen
                    ? 'bg-[#4478ea]/20 text-[#4478ea]'
                    : 'bg-black/5 dark:bg-[#eddeb7]/10 text-[#666666] dark:text-[#999999]'}`}>
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h4 className={`text-sm font-medium ${pollOpen ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-[#ea580c] dark:text-white'}`}>
                      Add a Poll
                    </h4>
                    <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                      {pollOpen ? 'Up to 5 options' : 'Let people vote on your yap'}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-[#666666] dark:text-[#999999] transition-transform ${pollOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* Builder expands inline, matching the Add Topic picker, so the
                  composer stays in one place instead of opening a sheet. */}
              <AnimatePresence>
                {pollOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-2 rounded-xl border border-black/10 dark:border-white/10 bg-[#F5F5F5] dark:bg-black/30 p-3 space-y-2">
                      {pollOptions.map((opt, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <Input
                            value={opt}
                            onChange={(e) => setPollOptions(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                            placeholder={`Option ${i + 1}`}
                            maxLength={MAX_POLL_OPTION_LENGTH}
                            // Colours are inline, not utilities: index.css is
                            // committed pre-compiled Tailwind, and the shades used
                            // here (dark:bg-black/40, and the text-[#ea580c] this
                            // was copied from) are not in it, so they rendered
                            // nothing and left the typed text invisible.
                            className="flex-1 h-10 border-black/10 dark:border-white/10 rounded-lg"
                            style={{
                              color: theme === 'dark' ? '#FFFFFF' : '#111111',
                              backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
                            }}
                          />
                          {pollOptions.length > MIN_POLL_OPTIONS && (
                            <button
                              type="button"
                              aria-label={`Remove option ${i + 1}`}
                              onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}
                              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/10"
                            >
                              <X className="w-4 h-4 text-[#666666] dark:text-[#999999]" />
                            </button>
                          )}
                        </div>
                      ))}

                      {pollOptions.length < MAX_POLL_OPTIONS && (
                        <button
                          type="button"
                          onClick={() => setPollOptions(prev => [...prev, ''])}
                          className="text-xs font-semibold text-[#4478ea] px-1 py-1"
                        >
                          + Add option
                        </button>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <span className="text-xs font-medium text-black dark:text-white">Runs for</span>
                        {Array.from({ length: maxPollDays }, (_, i) => i + 1).map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setPollDays(d)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${pollDays === d
                              ? 'bg-[#4478ea] text-white'
                              : 'bg-black/5 dark:bg-white/10 text-[#666666] dark:text-[#a0a0a0]'}`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>

                      {isAnonymous && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                          Anonymous yaps are deleted 24 hours after posting, and the
                          poll goes with them, so an anonymous poll can only run for
                          a day.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Helper text when friends-only is selected */}
            {visibility === 'friends' && (
              <div className="mb-4 p-3 rounded-xl bg-[#F5F5F5] dark:bg-[#121212] border border-black/10 dark:border-white/10">
                <p className="text-xs text-[#666666] dark:text-[#E5E5E5] flex items-center gap-2">
                  💡 Anything posted here will only be seen privately by people you are already connected with. If you'd like to make a public post, do it in the World YapTab.
                </p>
              </div>
            )}
            
            {/* Image Previews */}
            {selectedImages.length > 0 && (
              <div className="mb-4 grid grid-cols-2 gap-3">
                {selectedImages.map((img, index) => (
                  <div key={index} className="relative rounded-xl overflow-hidden">
                    <img src={img} alt={`Upload ${index + 1}`} className="w-full h-32 object-cover" />
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </motion.button>
                  </div>
                ))}
              </div>
            )}

            {/* Video Preview + Trim Picker */}
            {selectedVideo && videoPreviewUrl && (
              <div className="mb-4 rounded-xl overflow-hidden bg-black relative">
                {/* Bare video element. When the clip was physically cut by ffmpeg
                    (the common path) the blob IS the trimmed clip, so it just plays
                    — no clamp handlers. Clamp handlers only run in the metadata
                    fallback case (ffmpeg failed → original file + trim markers). */}
                <video
                  ref={previewVideoRef}
                  src={videoPreviewUrl}
                  controls
                  playsInline
                  muted={false}
                  preload="auto"
                  poster=""
                  onLoadedMetadata={(e: any) => {
                    const v = e.currentTarget;
                    // iOS WebKit shows a BLACK box for a blob/file video until a
                    // frame is decoded — nudge currentTime to force the first
                    // frame (of the trimmed section) to render as a thumbnail.
                    const start = videoTrimEndOverride !== null ? videoTrimStart : 0;
                    try { v.currentTime = Math.max(0.05, start); } catch (_) {}
                    // Best-effort autoplay with sound. iOS blocks autoplay-with-
                    // audio without a gesture, so this may reject — the first frame
                    // still renders (temp-file src) and the user can tap play.
                    v.play?.().catch(() => {});
                  }}
                  onTimeUpdate={(e: any) => {
                    if (videoTrimEndOverride === null) return;
                    const v = e.currentTarget;
                    if (v.currentTime >= videoTrimEnd || v.currentTime < videoTrimStart - 0.3) {
                      try { v.currentTime = videoTrimStart; } catch (_) {}
                    }
                  }}
                  className="w-full max-h-72 object-contain bg-black"
                />
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={removeVideo}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center z-10"
                >
                  <X className="w-4 h-4 text-white" />
                </motion.button>

                {/* Trim Video — always available when a video is selected.
                    Opens the same trimmer used for portfolio media. */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowVideoTrimmer(true)}
                  className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4478ea] text-white text-xs font-bold shadow-lg backdrop-blur-sm"
                >
                  <Film className="w-3.5 h-3.5" />
                  {videoTrimEndOverride !== null ? 'Edit Trim' : 'Trim Video'}
                </motion.button>

                {/* Trim picker — only when the video exceeds 3 minutes. The
                    user drags the slider to choose the START of the 3-min
                    window; END = START + 180s (clamped to full duration). */}
                {videoNeedsTrim && (
                  <div className="px-4 py-3 bg-black/80 text-white">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="font-semibold text-[#4478ea]">
                        Trim to 3 min
                      </span>
                      <span className="opacity-75 tabular-nums">
                        {Math.floor(videoTrimStart / 60)}:
                        {String(Math.floor(videoTrimStart % 60)).padStart(2, '0')}
                        {' → '}
                        {Math.floor(videoTrimEnd / 60)}:
                        {String(Math.floor(videoTrimEnd % 60)).padStart(2, '0')}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, videoDuration - MAX_VIDEO_SECONDS)}
                      step={1}
                      value={videoTrimStart}
                      onChange={(e) => { setVideoTrimStart(parseFloat(e.target.value)); setVideoTrimEndOverride(null); }}
                      className="w-full accent-[#4478ea]"
                    />
                    <p className="text-[10px] opacity-60 mt-1">
                      Full video is {Math.floor(videoDuration / 60)}:
                      {String(Math.floor(videoDuration % 60)).padStart(2, '0')}.
                      Drag to pick which 3-minute window plays.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Free video trimmer (same screen as portfolio media). Lets the user
                trim to ANY shorter length — not just a fixed 3-minute window. */}
            {selectedVideo && videoPreviewUrl && (
              <MediaTrimmerModal
                isOpen={showVideoTrimmer}
                onClose={() => setShowVideoTrimmer(false)}
                mediaUrl={videoPreviewUrl}
                mediaBlob={selectedVideo}
                mediaType="video"
                title="Trim"
                aspectRatio={9 / 16}
                maxDurationSec={MAX_VIDEO_SECONDS}
                videoOnly
                onSave={async (data) => {
                  let ts = typeof data.trimStart === 'number' ? data.trimStart : 0;
                  let te = typeof data.trimEnd === 'number' ? data.trimEnd : videoDuration;
                  // Keep the 3-minute ceiling, but any shorter trim is allowed.
                  if (te - ts > MAX_VIDEO_SECONDS) {
                    te = ts + MAX_VIDEO_SECONDS;
                    toast.info('Trimmed to the 3-minute maximum.');
                  }
                  const dur = Math.max(0, te - ts);
                  const sourceFile = selectedVideo;
                  // If the user didn't actually trim (whole clip), skip processing.
                  if (!sourceFile || (ts <= 0.05 && te >= videoDuration - 0.05)) {
                    setVideoTrimStart(0);
                    setVideoTrimEndOverride(null);
                    setShowVideoTrimmer(false);
                    return;
                  }
                  const toastId = toast.loading('Processing video…');
                  try {
                    // Physically cut the file with ffmpeg.wasm — the trimmed blob
                    // is what gets uploaded (no original + trim markers).
                    const { trimVideo } = await import('../services/videoTrim');
                    const { blob } = await trimVideo(sourceFile, ts, dur, (p) => {
                      toast.loading(`Processing video… ${Math.round(p * 100)}%`, { id: toastId });
                    });
                    const baseName = (sourceFile.name || 'video').replace(/\.[^.]+$/, '');
                    const trimmedFile = new File([blob], `${baseName}_trim.mp4`, { type: 'video/mp4' });
                    setSelectedVideo(trimmedFile);
                    await applyVideoPreview(trimmedFile);
                    setVideoDuration(dur);
                    // File is already cut → no playback-clamp metadata needed.
                    setVideoTrimStart(0);
                    setVideoTrimEndOverride(null);
                    toast.success('Video trimmed', { id: toastId });
                  } catch (err) {
                    console.error('[CreateYap] ffmpeg trim failed, falling back to trim markers:', err);
                    // Fallback so posting never breaks: keep the original file but
                    // store the trim window so every player clamps to it.
                    setVideoTrimStart(ts);
                    setVideoTrimEndOverride(te);
                    toast.error('Couldn’t process the cut on-device — using trim markers instead.', { id: toastId });
                  } finally {
                    setShowVideoTrimmer(false);
                  }
                }}
              />
            )}

            </div>{/* end scrollable body */}

            {/* Action Bar — always visible at bottom */}
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-black/10 dark:border-white/20 bg-light-pattern dark:bg-[#1a1a1a] rounded-b-3xl">
              <div className="flex items-center gap-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleMediaUpload}
                  disabled={(!canAddImage && !canAddVideo) || isRecording}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${(canAddImage || canAddVideo) && !isRecording
                    ? 'bg-gray-200 dark:bg-slate-800 text-[#ea580c] dark:text-[#4478ea] hover:bg-gray-300 dark:hover:bg-slate-700'
                    : 'bg-gray-100 dark:bg-slate-900 text-[#999999] cursor-not-allowed'
                    }`}
                >
                  {selectedVideo ? <Film className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
                  <span className="text-sm hidden sm:inline">
                    {selectedVideo
                      ? 'Video attached'
                      : `Media (${selectedImages.length}/${MAX_IMAGES})`}
                  </span>
                </motion.button>

                {!voicePreviewUrl && (
                  <VoiceRecorderUI 
                    isRecording={isRecording}
                    isPaused={isPaused} // ⏸️ Added
                    duration={duration}
                    maxDuration={10000}
                    onStart={startRecording}
                    onStop={stopRecording}
                    onCancel={reset}
                    onPause={pauseRecording}   // ⏸️ Added
                    onResume={resumeRecording} // ⏸️ Added
                  />
                )}
              </div>
              <input
                type="file"
                ref={mediaInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,video/*"
              />

              <Button
                variant="ghost"
                onClick={async () => {
                  if (isRecording) {
                    stopRecording();
                    return;
                  }
                  await handlePost();
                }}
                disabled={(content.trim().length === 0 && !voiceBlob && !isRecording && !selectedVideo) || (charLimit !== null && content.length > charLimit)}
                className="bg-brand-gradient hover:opacity-90 text-white disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2 rounded-xl font-bold transition-all active:scale-95 shadow-lg"
              >
                <Send className="w-4 h-4 mr-2" />
                {isRecording ? 'Stop' : (voiceBlob ? 'Post Voice' : 'Post Yap')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
